import axiosInstance from './axiosInstance';

const normalizeList = (data, key) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data[key])) return data[key];
  if (data && Array.isArray(data.data)) return data.data;
  return [];
};

// Normalize transaction/payment history items to { date, time, amount, description, type, paymentMode }
function normalizeTransactions(list) {
  if (!Array.isArray(list)) return list && typeof list === 'object' ? [list] : [];
  return list.map((t) => {
    if (!t || typeof t !== 'object') return null;
    const createdAt = t.created_at ?? t.settled_at ?? t.paid_at ?? t.date;
    let date = t.date ?? '';
    let time = t.time ?? '';
    if (createdAt) {
      const d = new Date(createdAt);
      if (!date && !Number.isNaN(d.getTime())) date = d.toISOString().slice(0, 10);
      if (!time && !Number.isNaN(d.getTime())) {
        time = d.toLocaleTimeString('en-IN', { hour12: true, hour: '2-digit', minute: '2-digit', second: '2-digit' });
      }
    }
    return {
      date: date || '—',
      time: time || '—',
      amount: parseFloat(t.amount) || 0,
      description: t.description ?? t.notes ?? 'Payment',
      type: (t.type ?? t.payment_type ?? 'payment').toLowerCase(),
      paymentMode: t.payment_mode ?? t.paymentMode ?? 'cash',
    };
  }).filter(Boolean);
}

const mapExpenseFromApi = (e) => {
  if (!e) return null;
  const totalAmount = parseFloat(e.total_amount) || 0;
  const paidAmount = parseFloat(e.paid_amount) || 0;
  const pendingAmount = Math.max(0, totalAmount - paidAmount);
  return {
    id: e.id,
    date: e.expense_date ? e.expense_date.slice(0, 10) : e.date?.slice(0, 10) ?? '',
    expense_date: e.expense_date ?? e.date,
    category: e.category ?? '',
    subCategory: e.sub_category ?? e.subCategory ?? '',
    sub_category: e.sub_category ?? e.subCategory,
    description: e.description ?? '',
    paymentMode: e.payment_mode ?? e.paymentMode ?? 'cash',
    payment_mode: e.payment_mode ?? e.paymentMode,
    totalAmount,
    total_amount: totalAmount,
    paidAmount,
    paid_amount: paidAmount,
    pendingAmount: e.pending_amount != null ? parseFloat(e.pending_amount) : pendingAmount,
    status: (e.status || (pendingAmount === 0 ? 'paid' : 'pending')).toLowerCase(),
    vendorName: e.vendor_name ?? e.vendorName ?? '',
    staffName: e.staff_name ?? e.staffName ?? '',
    transactions: normalizeTransactions(e.transactions ?? e.payment_history ?? e.settlements),
    attachments: Array.isArray(e.attachments) ? e.attachments : [],
    selectedProductIds: Array.isArray(e.product_ids) ? e.product_ids : (e.selectedProductIds ?? []),
    product_ids: Array.isArray(e.product_ids) ? e.product_ids : [],
    createdAt: e.created_at ?? e.createdAt,
    updatedAt: e.updated_at ?? e.updatedAt,
  };
};

const buildExpenseFormData = (data, existingAttachments = []) => {
  const formData = new FormData();
  formData.append('expense_date', (data.date || data.expense_date || '').toString().slice(0, 10));
  formData.append('category', (data.category || '').toString().trim());
  formData.append('sub_category', (data.subCategory || data.sub_category || '').toString().trim());
  formData.append('description', (data.description || '').toString().trim());
  formData.append('payment_mode', (data.paymentMode || data.payment_mode || 'cash').toString());
  const total = Number(data.totalAmount ?? data.total_amount ?? 0);
  formData.append('total_amount', total);
  const paid = Number(data.paidAmount ?? data.paid_amount ?? 0);
  if (paid >= 0) formData.append('paid_amount', paid);

  const productIds = data.selectedProductIds ?? data.product_ids;
  if (Array.isArray(productIds) && productIds.length > 0) {
    formData.append('product_ids', JSON.stringify(productIds));
  }

  const files = data.attachmentsFiles ?? data.attachments;
  if (Array.isArray(files)) {
    files.filter((f) => f instanceof File).slice(0, 5).forEach((file) => {
      formData.append('attachments', file);
    });
  }
  return formData;
};

const buildUpdateFormData = (data, existingAttachments = []) => {
  const formData = new FormData();
  if (data.date != null) formData.append('expense_date', data.date.toString().slice(0, 10));
  if (data.category != null) formData.append('category', data.category.toString().trim());
  if (data.subCategory != null) formData.append('sub_category', data.subCategory.toString().trim());
  if (data.description != null) formData.append('description', data.description.toString().trim());
  if (data.paymentMode != null) formData.append('payment_mode', data.paymentMode.toString());
  if (data.totalAmount != null) formData.append('total_amount', Number(data.totalAmount));
  if (data.paidAmount != null) formData.append('paid_amount', Number(data.paidAmount));

  const productIds = data.selectedProductIds ?? data.product_ids;
  if (Array.isArray(productIds) && productIds.length > 0) {
    formData.append('product_ids', JSON.stringify(productIds));
  }

  const newFiles = data.newAttachments ?? data.attachmentsFiles ?? [];
  if (Array.isArray(newFiles)) {
    newFiles.filter((f) => f instanceof File).slice(0, 5).forEach((file) => {
      formData.append('newAttachments', file);
    });
  }
  return formData;
};

export const fetchExpenses = async () => {
  const response = await axiosInstance.get('/expenses');
  const list = normalizeList(response.data, 'expenses') || normalizeList(response.data, 'data') || (Array.isArray(response.data) ? response.data : []);
  return list.map(mapExpenseFromApi).filter(Boolean);
};

export const fetchExpenseStats = async () => {
  const response = await axiosInstance.get('/expenses/stats');
  const raw = response.data?.stats ?? response.data?.data ?? response.data;
  if (!raw) return { total: 0, paid: 0, pending: 0, avgExpense: 0, count: 0 };
  return {
    total: Number(raw.total ?? raw.total_amount ?? 0),
    paid: Number(raw.paid ?? raw.paid_amount ?? 0),
    pending: Number(raw.pending ?? raw.pending_amount ?? 0),
    avgExpense: Number(raw.avg_expense ?? raw.avgExpense ?? 0),
    count: Number(raw.count ?? raw.total_count ?? 0),
  };
};

export const fetchExpenseById = async (id) => {
  const response = await axiosInstance.get(`/expenses/${id}`);
  const raw = response.data?.expense ?? response.data?.data ?? response.data;
  return mapExpenseFromApi(raw);
};

export const createExpense = async (data) => {
  const formData = buildExpenseFormData(data);
  const response = await axiosInstance.post('/expenses', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const created = response.data?.expense ?? response.data?.data ?? response.data;
  return mapExpenseFromApi(created);
};

export const updateExpense = async (id, data) => {
  const formData = buildUpdateFormData(data);
  const response = await axiosInstance.put(`/expenses/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const updated = response.data?.expense ?? response.data?.data ?? response.data;
  return mapExpenseFromApi(updated);
};

export const deleteExpense = async (id) => {
  await axiosInstance.delete(`/expenses/${id}`);
};

export const settlePayment = async (expenseId, payload) => {
  const amount = Number(payload.amount);
  const paymentMode = (payload.payment_mode ?? payload.paymentMode ?? 'cash').toString().trim() || 'cash';
  if (!Number.isFinite(amount) || amount < 0.01) {
    throw new Error('Amount must be at least 0.01');
  }
  const response = await axiosInstance.post(`/expenses/${expenseId}/settle-payment`, {
    amount,
    payment_mode: paymentMode,
  });
  const raw = response.data?.expense ?? response.data?.data ?? response.data;
  const updated = mapExpenseFromApi(raw);
  return updated;
};

export const deleteExpenseAttachment = async (expenseId, attachmentId) => {
  await axiosInstance.delete(`/expenses/${expenseId}/attachments/${attachmentId}`);
};
